import { generatePath, PathParam } from 'react-router-dom';
import { UiPath } from './index';

// TODO: move to sdk. QueryParams should be where useQueryParams is

export type UiPathParams<
  Path extends UiPath = UiPath,
  Value extends string | number = string | number,
> = {
  [key in PathParam<Path>]: Value;
};

export type QueryParams<Value extends string | number = string | number> =
  Record<string, Value>;

/**
 * Takes a tokenized path string and object of all parameters and gives
 * back a string with those parameters interpolated into the path whose keys
 * match the tokens in the path and the rest appended as query parameters.
 *
 * @param path
 * @param allParams
 */
export const withParams = <
  Path extends UiPath,
  PathParams extends UiPathParams<Path>,
  AllParams extends PathParams & Partial<QueryParams>,
>(
  path: Path,
  allParams: AllParams,
) => {
  const { pathParams, queryParams } = segregateParams(path, allParams);

  const pathname = withPathParams(path, pathParams);

  const search = new URLSearchParams(
    queryParams as QueryParams<string>,
  ).toString();

  return pathname + (search ? `?${search}` : '');
};

/**
 * Wrapper around react-router's generatePath function that just passes
 * arguments through without modification but is more lenient in terms of the type
 * for parameters — generatePath is strict and expects all parameter values
 * to be passed as strings while in reality numbers are ok too (they end up being
 * coerced to string via native .toString() method).
 *
 * @param path
 * @param pathParams
 */
const withPathParams = <
  Path extends UiPath,
  PathParams extends UiPathParams<Path>,
>(
  path: Path,
  pathParams: PathParams,
) => {
  const stringPathParams = Object.fromEntries(
    Object.entries(pathParams).map(([key, value]) => [key, String(value)]),
  ) as Parameters<typeof generatePath<Path>>[1];

  return generatePath(path, stringPathParams);
};

/**
 * Takes a tokenized path string and object of all parameters and gives
 * back parameters partitioned into two:
 *  - `pathParams` includes those params whose keys are found in the given `path` as tokens,
 *  - `queryParams` includes all the rest.
 *
 * @param path
 * @param allParams
 */
const segregateParams = <
  Path extends UiPath,
  PathParams extends UiPathParams<Path>,
  AllParams extends PathParams & Partial<QueryParams>,
>(
  path: Path,
  allParams: AllParams,
): {
  pathParams: PathParams;
  queryParams: QueryParams;
} => {
  const pathParams = {} as PathParams;
  const queryParams: QueryParams = { ...allParams };

  path.split('/').forEach((segment) => {
    if (segment === '*') {
      (pathParams as any)['*'] = allParams['*'];
      delete queryParams['*'];
    } else {
      const [, key] = segment.match(pathTokenPattern) || [];
      if (key) {
        (pathParams as any)[key] = allParams[key];
        delete queryParams[key];
      }
    }
  });

  Object.entries(queryParams).forEach(([key, value]) => {
    if (value === undefined) {
      delete queryParams[key];
    }
  });

  return { pathParams, queryParams };
};

// Source: react-router
const pathTokenPattern = /^:([\w-]+)(\??)$/;
